import { z } from "zod";

/**
 * Which lines of the trigger the lead wrote this time, and which are a sign-off, a signature, a
 * disclaimer or a footer a client or gateway appended (`docs/2026-10-14-message-regions.md`).
 *
 * A call of its own labels the lines (`agents/message-regions.md`, the `regions` stage); this module
 * numbers them, checks the labels and keeps the result. Asking for them inside the classification call
 * cost accuracy on the classifier's asks (`docs/2026-10-14-message-regions.md`, "Measured"). No keyword, sign-off list or client marker decides a region here: an Outlook reply whose
 * confidentiality notice held "If you are not the person to whom this message is addressed" was read
 * as a condition the lead set, because the host's own signature cut knew only Gmail's marker.
 *
 * Pure and workflow-safe: the drafting pipeline runs it inside a Temporal workflow.
 */

/** The patch pair; the worker answers it per Bison workspace (`classifier-split-activation.ts`). */
export const MESSAGE_REGIONS_PATCH = "message-regions-v1";
export const MESSAGE_REGIONS_OFF_PATCH = "message-regions-off-v1";

/** The most lines one call labels. A longer trigger's later lines are outside the message. */
export const MESSAGE_LINE_LIMIT = 200;

const MessageRegionKindSchema = z.enum([
  "message",
  "sign_off",
  "signature",
  "disclaimer",
  "automatic_footer",
  "quoted",
]);
export type MessageRegionKind = z.infer<typeof MessageRegionKindSchema>;

const LineIndex = z.number().int().min(0).max(100_000);

/** One non-blank line of the trigger's authored text, by its index among all of that text's lines. */
const MessageLineSchema = z.strictObject({ line: LineIndex, text: z.string().min(1).max(2_000) });

/** What the regions call is handed: present only where the workspace's switch is on. */
export const MessageRegionsAskSchema = z.strictObject({
  lines: z.array(MessageLineSchema).min(1).max(MESSAGE_LINE_LIMIT),
});
export type MessageRegionsAsk = z.infer<typeof MessageRegionsAskSchema>;

const MessageRegionSchema = z.strictObject({
  first_line: LineIndex,
  last_line: LineIndex,
  kind: MessageRegionKindSchema,
});

/** What the call returns: ranges over the handed line numbers, in order. */
export const MessageRegionsProposalSchema = z.strictObject({
  regions: z.array(MessageRegionSchema).min(1).max(MESSAGE_LINE_LIMIT),
});
export type MessageRegionsProposal = z.infer<typeof MessageRegionsProposalSchema>;

const InvalidReasonSchema = z.enum(["missing", "out_of_order", "unknown_line", "line_uncovered"]);

/**
 * What a run keeps (`run-details/<hash>.message-regions.json`). `valid`: the checked regions and the
 * lead's own words they leave. `invalid`: the labels failed the host's check, and no rule reads the
 * lead's own words from them or from anything else.
 */
export const MessageRegionsRecordSchema = z.discriminatedUnion("state", [
  z.strictObject({
    version: z.literal(1),
    state: z.literal("valid"),
    regions: z.array(MessageRegionSchema).min(1).max(MESSAGE_LINE_LIMIT),
    /** The message regions' lines as the trigger has them, blank lines inside a region kept. */
    message: z.string(),
    /** True when the trigger had more non-blank lines than one call labels. */
    truncated: z.boolean(),
  }),
  z.strictObject({
    version: z.literal(1),
    state: z.literal("invalid"),
    reason: InvalidReasonSchema,
  }),
]);
export type MessageRegionsRecord = z.infer<typeof MessageRegionsRecordSchema>;

/** The trigger's non-blank lines, numbered by their place among all its lines; null when none. */
export function messageRegionsAsk(text: string | null): MessageRegionsAsk | null {
  if (text === null) return null;
  const lines = text
    .split("\n")
    .map((raw, line) => ({ line, text: raw.trim().slice(0, 2_000) }))
    .filter((row) => row.text.length > 0)
    .slice(0, MESSAGE_LINE_LIMIT);
  return lines.length > 0 ? MessageRegionsAskSchema.parse({ lines }) : null;
}

function invalid(reason: z.infer<typeof InvalidReasonSchema>): MessageRegionsRecord {
  return { version: 1, state: "invalid", reason };
}

/**
 * The host's check of the call's labels against the lines it was handed: ranges in order, each
 * bounded by handed lines, together labelling every handed line exactly once. Blank lines between
 * handed lines need no label. A label that fails any of these is not partly trusted.
 */
export function checkMessageRegions(
  text: string,
  ask: MessageRegionsAsk,
  proposal: MessageRegionsProposal | undefined,
): MessageRegionsRecord {
  if (!proposal) return invalid("missing");
  const handed = new Set(ask.lines.map((row) => row.line));
  // Ranges strictly ascending cannot overlap, so a line is labelled at most once.
  let previousLast = -1;
  for (const region of proposal.regions) {
    if (region.first_line > region.last_line || region.first_line <= previousLast)
      return invalid("out_of_order");
    if (!handed.has(region.first_line) || !handed.has(region.last_line))
      return invalid("unknown_line");
    previousLast = region.last_line;
  }
  const labelled = (line: number) =>
    proposal.regions.some((region) => line >= region.first_line && line <= region.last_line);
  if (ask.lines.some((row) => !labelled(row.line))) return invalid("line_uncovered");
  const all = text.split("\n");
  const message = proposal.regions
    .filter((region) => region.kind === "message")
    .map((region) =>
      all
        .slice(region.first_line, region.last_line + 1)
        .join("\n")
        .trim(),
    )
    .filter(Boolean)
    .join("\n\n");
  const lastHanded = ask.lines.at(-1)?.line ?? -1;
  const truncated = all.slice(lastHanded + 1).some((line) => line.trim().length > 0);
  return { version: 1, state: "valid", regions: proposal.regions, message, truncated };
}

/** The lines to hand the regions call, asking the switch only for a trigger with lines. */
export function regionsToAsk(
  text: string | null,
  switchOn: () => boolean,
): MessageRegionsAsk | null {
  const ask = messageRegionsAsk(text);
  return ask && switchOn() ? ask : null;
}

/** The run's record of the call's labels, as a field to merge; empty where the call was not asked. */
export function regionsField(
  text: string | null,
  ask: MessageRegionsAsk | null,
  proposal: MessageRegionsProposal | undefined,
): { message_regions?: MessageRegionsRecord } {
  return ask && text !== null ? { message_regions: checkMessageRegions(text, ask, proposal) } : {};
}

/**
 * The lead's own words for a host rule: the message regions where the call labelled them, null where
 * the labels failed (nothing is guessed in their place), and undefined where the call was never asked,
 * so the rule keeps what it read before regions existed.
 */
export function ownWordsFromRegions(
  record: MessageRegionsRecord | undefined,
): string | null | undefined {
  if (record === undefined) return undefined;
  if (record.state === "invalid") return null;
  return record.message || null;
}

/** Message regions are asked of the regions call only; true when this call is handed lines. */
export function asksMessageRegions(
  stage: string,
  runtime: { readonly message_regions?: MessageRegionsAsk | undefined },
): boolean {
  const asked = runtime.message_regions !== undefined;
  if (asked !== (stage === "regions"))
    throw new Error("Message regions are handed to the regions call, and only to it");
  return asked;
}
